import React from 'react'
import { thaloColors } from '@/styles/thalo-colors'
import { LineChartResponse } from '@/components/charts/lineChartResponse'
import { ChartProps, TrendlineEntry } from '@/components/charts/chartTypes'

const brandColors = [
  thaloColors.primary.deepNavy,
  thaloColors.primary.brightBlue,
  thaloColors.primary.green,
  thaloColors.primary.pinkRed,
  thaloColors.primary.mediumBlue,
  thaloColors.primary.skyBlue,
  thaloColors.primary.lightBlue,
  thaloColors.primary.softLavender,
  thaloColors.primary.deepCyan,
  thaloColors.primary.violet,
] as const

const secondaryColors = [
  thaloColors.secondary.navyMuted,
  thaloColors.secondary.blueMuted,
  thaloColors.secondary.greenMuted,
  thaloColors.secondary.pinkMuted,
  thaloColors.secondary.steelBlue,
] as const

export { brandColors, secondaryColors }

/**
 * Removes exact duplicate [timestamp, value] pairs and sorts the result
 * chronologically. "Duplicate" means identical timestamp AND identical value —
 * two different readings at the same instant are preserved.
 */
export function dedupeAndSortSeriesData(data: [string, number][]): [string, number][] {
  const deduped = data.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t[0] === item[0] && t[1] === item[1])
  )
  return deduped.sort(
    (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime()
  )
}

export interface UseChartSeriesParams {
  results: Array<{ isSuccess: boolean; data?: LineChartResponse }>
  effectiveShowTemperature: boolean
  temperature: { isPending: boolean; isSuccess: boolean; data?: LineChartResponse }
  temperatureUnit: string
  canShowTemperatureOnPrimaryYAxis: boolean
  combineSeries?: ChartProps['combineSeries']
  trendlineData?: TrendlineEntry[]
}

/**
 * Merges multiple query results into a single list of `LineChartResponse`
 * objects, applying deduplication, optional series combining, outdoor
 * temperature overlay, and trendline calculations.
 */
export function useChartSeries({
  results,
  effectiveShowTemperature,
  temperature,
  temperatureUnit,
  canShowTemperatureOnPrimaryYAxis,
  combineSeries,
  trendlineData,
}: UseChartSeriesParams): LineChartResponse[] {
  return React.useMemo(() => {
    const merged: LineChartResponse[] = []

    results.forEach(res => {
      if (res.isSuccess && res.data && res.data.series.length !== 0) {
        merged.push({
          ...res.data,
          series: res.data.series.map(ser => ({
            ...ser,
            data: dedupeAndSortSeriesData(ser.data),
          })),
        })
      }
    })

    if (merged.length === 0) return merged

    if (combineSeries) {
      const allSeries = merged.flatMap(response =>
        response.series.map(ser => ({
          name: ser.name,
          data: ser.data.map(([timestamp, value]) => ({ timestamp, value })),
        }))
      )
      const combinedSeries = combineSeries(allSeries)
      merged[0] = {
        ...merged[0],
        series: combinedSeries.map(ser => ({
          ...ser,
          data: ser.data.map(d => [d.timestamp, d.value] as [string, number]),
          type: 'line',
          lineStyle: { color: brandColors[combinedSeries.indexOf(ser) % brandColors.length] },
        })),
      }
    }

    if (effectiveShowTemperature && temperature.isSuccess && temperature.data) {
      const outdoorSeries = temperature.data.series[0]
      const convertedData = outdoorSeries.data.map(([timestamp, value]: [string, number]) => {
        const celsiusValue =
          temperatureUnit === 'degC' && value > 50 ? (value - 32) * 5 / 9 : value
        return [timestamp, celsiusValue] as [string, number]
      })
      merged.push({
        ...temperature.data,
        series: [{
          ...outdoorSeries,
          data: convertedData,
          yAxisIndex: canShowTemperatureOnPrimaryYAxis || merged.length === 0 ? 0 : 1,
          itemStyle: { color: thaloColors.accent.peach },
          lineStyle: { color: thaloColors.accent.peach },
        }],
      })
    }

    if (trendlineData && merged.length > 0) {
      const firstSeries = merged[0].series[0]
      if (firstSeries.data.length > 0) {
        const startTime = new Date(firstSeries.data[0][0]).getTime()
        const endTime = new Date(firstSeries.data[firstSeries.data.length - 1][0]).getTime()
        const timeStep = (endTime - startTime) / (firstSeries.data.length - 1)

        trendlineData.forEach((trendline, index) => {
          const trendlinePoints: [string, number][] = firstSeries.data.map((point, i) => {
            const x_numeric = (i * timeStep) / 1000
            const y_pred = trendline.data.slope * x_numeric + trendline.data.intercept
            return [point[0], y_pred]
          })
          const trendLineColor = secondaryColors[index % secondaryColors.length]
          merged[0].series.push({
            name: `${trendline.sensor} ${trendline.metric} Trend (${trendline.data.rate_of_change.toFixed(1)}%/${trendline.data.denominator})`,
            data: trendlinePoints,
            type: 'line',
            lineStyle: { type: 'dashed', width: 2, color: trendLineColor, opacity: 0.2, dash: [10, 5] },
            itemStyle: { color: trendLineColor, opacity: 0.2 },
          })
        })
      }
    }

    return merged
  }, [results, effectiveShowTemperature, temperature, canShowTemperatureOnPrimaryYAxis, combineSeries, trendlineData, temperatureUnit])
}
